import { useEffect, useState } from "react";
import { Trash2, Pencil, X, Star } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const EMPTY = { name: "", text: "", source: "Google", rating: 5, published: true };

export default function ReviewsManager() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/testimonials").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing.name || !editing.text) { toast.error("Name and review text are required"); return; }
    if (editing.id) await api.put(`/admin/testimonials/${editing.id}`, editing);
    else await api.post("/admin/testimonials", editing);
    toast.success("Saved");
    setEditing(null); load();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this review? This cannot be undone.")) return;
    await api.delete(`/admin/testimonials/${id}`); load(); };

  return (
    <div data-testid="portal-reviews-manager">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-heading font-extrabold text-[#0B1B3D] text-2xl md:text-3xl">Reviews</h1>
        <button onClick={() => setEditing({ ...EMPTY })} className="btn-primary !py-2.5 !px-4 !text-xs" data-testid="review-add-btn">Add Review</button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {items.map((r) => (
          <div key={r.id} className="bg-white border border-slate-200 p-6" data-testid={`review-item-${r.id}`}>
            <div className="flex gap-1 mb-3">{[...Array(r.rating || 5)].map((_, i) => <Star key={i} className="w-4 h-4 text-[#005AE0] fill-[#005AE0]" />)}</div>
            <p className="text-slate-600 text-sm mb-4">“{r.text}”</p>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-heading font-bold text-[#0B1B3D] text-sm">{r.name}</p>
                <p className="text-xs text-slate-400">{r.source} {!r.published && "• hidden"}</p>
              </div>
              <div className="flex gap-1">
                <button onClick={() => setEditing(r)} className="p-2 border border-slate-200 text-slate-500" data-testid={`review-edit-${r.id}`}><Pencil className="w-4 h-4" /></button>
                <button onClick={() => remove(r.id)} className="p-2 border border-slate-200 text-slate-400 hover:text-red-500" data-testid={`review-delete-${r.id}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="text-slate-400 text-sm col-span-full py-8">No reviews yet. Add real customer reviews from Google, Facebook or Nextdoor.</p>}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="review-edit-modal">
          <div className="bg-white w-full max-w-lg p-6 md:p-8">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-heading font-bold text-[#0B1B3D] text-xl">{editing.id ? "Edit Review" : "Add Review"}</h2>
              <button onClick={() => setEditing(null)} data-testid="review-modal-close"><X className="w-5 h-5 text-slate-400" /></button>
            </div>
            <label className="field-label">Customer Name</label>
            <input className="field mb-4" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="review-name-input" />
            <label className="field-label">Review Text</label>
            <textarea className="field mb-4" rows={4} value={editing.text} onChange={(e) => setEditing({ ...editing, text: e.target.value })} data-testid="review-text-input" />
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <label className="field-label">Source</label>
                <select className="field" value={editing.source} onChange={(e) => setEditing({ ...editing, source: e.target.value })} data-testid="review-source-select">
                  {["Google", "Facebook", "Nextdoor", "Direct"].map((s) => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="field-label">Rating</label>
                <select className="field" value={editing.rating} onChange={(e) => setEditing({ ...editing, rating: Number(e.target.value) })} data-testid="review-rating-select">
                  {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n} stars</option>)}
                </select>
              </div>
            </div>
            <button onClick={save} className="btn-primary w-full" data-testid="review-save-btn">Save</button>
          </div>
        </div>
      )}
    </div>
  );
}
